/**
 * Pagination component
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import React, { useEffect, useState } from 'react';
import { isUndefined } from 'lodash';

// CSS Style for this module
import styles from './pagination.module.scss';

/**
 * Pagination for Accordion props
 */
interface PaginationProps {
	/**
	 * Total counts of items that will be displayed
	 */
	itemCounts: number;
	/**
	 * Chosen page to be displayed
	 * If provided, this will be the default page to be rendered
	 */
	selectedPage?: number;
	/**
	 * Max amount of items to be displayed on one page
	 */
	paginationLimit?: number;
	/**
	 * Maximum number of visible pages
	 */
	maxVisiblePages?: number;
	/**
	 * Click handler
	 */
	handleClick?: (value: number) => void;
}

/**
 * UI element that divide list of items into pages
 * @param {PaginationProps} Prop
 * @returns  a series of numbers that indicates the current page of the items
 */
export const Pagination = ({
	itemCounts,
	paginationLimit = 3,
	selectedPage,
	maxVisiblePages = 5,
	handleClick,
}: PaginationProps) => {
	const [curPage, setCurPage] = useState<number>(selectedPage || 1);
	const [visiblePages, setVisiblePages] = useState<number[]>([]);
	const pageCounts = Math.ceil(itemCounts / paginationLimit);

	useEffect(() => {
		if (!isUndefined(selectedPage)) setCurPage(selectedPage);
	}, [selectedPage]);

	// Calculate the range of visible pages based on the current page
	useEffect(() => {
		const calculateVisiblePages = (selected: number) => {
			const nPages = pageCounts;
			const lRange = maxVisiblePages;

			const nRange = Math.ceil(selected / lRange);
			const start = (nRange - 1) * lRange + 1;
			const end = Math.min(start + lRange - 1, nPages);

			return Array.from({ length: end - start + 1 }, (_, i) => start + i);
		};
		setVisiblePages(calculateVisiblePages(curPage));
	}, [curPage, maxVisiblePages, pageCounts]);

	const handleButtonClick = (targetPage: number) => {
		if (targetPage > pageCounts || targetPage <= 0) {
			return;
		}
		if (handleClick) handleClick(targetPage);
		setCurPage(targetPage);
	};

	return (
		<div className={styles.container}>
			<button
				key={'Prev'}
				onClick={() => handleButtonClick(curPage - 1)}
				className={styles.button}
				style={{ visibility: curPage == 1 ? 'hidden' : 'initial' }}
			>
				Prev
			</button>
			<div className={styles.list}>
				{visiblePages.map(num => {
					return (
						<a
							className={[styles.item, num === curPage ? styles.selected : ''].join(' ')}
							key={num}
							onClick={() => {
								setCurPage(num);
								if (handleClick) handleClick(num);
							}}
						>
							{num}
						</a>
					);
				})}
			</div>
			<button
				key={'Next'}
				onClick={() => handleButtonClick(curPage + 1)}
				className={styles.button}
				style={{ visibility: curPage == pageCounts ? 'hidden' : 'initial' }}
			>
				Next
			</button>
		</div>
	);
};
